import moment from 'moment';
import * as types from 'constants/ActionTypes';
import { hourDescription, hasMultipleCarriers } from 'utils/Reserbus';
import dataFinder from '@/store/dataFinder';
import { finalPrice } from 'utils/trips/finalPricing';

/**
 * BOOK-168: the final-arm figure of a bus trip's pricing (`totalWithMarkup ?? total`), recorded
 * beside the base one so that `ProviderList` picks either at render. No gate is read here: the
 * arm is resolved on screen, after GrowthBook loads, never frozen at dispatch.
 *
 * Only a figure that differs from the base is recorded, so a payload without companions (the
 * backend off, or a trip without a markup rule) stores exactly what it stored before.
 *
 * @param {object} pricing - The trip's `pricing` or `roundTripPricing`.
 * @returns {number|undefined} The final figure, or undefined when it is the base total.
 */
const finalValueOf = (pricing) => {
  const value = finalPrice(pricing, true);

  return value === pricing.total ? undefined : Number(value);
};

/**
 * The provider entry for one bus trip: its line's details and the trip's prices. Both the base
 * figures and, when the trip carries companions, their final-arm counterparts are recorded as
 * numbers, and `ProviderList` picks one set at render. Flights and mixed itineraries record no
 * final figure, because their totals include airfares outside the markup change.
 *
 * @param {object} trip - A parsed bus trip, or a mixed itinerary's bus fragment.
 * @returns {object} The provider entry.
 */
function getBusProviderProperties(trip) {
  const { lineId, providerId, duration, departure, service, pricing, roundTripPricing } = trip;
  const { total, providerDiscount } = pricing;
  const { total: roundTotal, providerDiscount: roundProviderDiscount } = roundTripPricing;
  const line = dataFinder('lines', lineId);
  const departureHourDescription = hourDescription(moment(departure).hour());
  const lowestFinalPrice = finalValueOf(pricing);
  const roundLowestFinalPrice = finalValueOf(roundTripPricing);

  return {
    providerDiscount,
    roundProviderDiscount,
    service: service.toUpperCase(),
    transportType: 'bus',
    id: providerId,
    name: line.name,
    transporterName: line.transporterName,
    ally: line.ally,
    lowestPrice: total,
    roundLowestPrice: roundTotal,
    ...(lowestFinalPrice !== undefined && { lowestFinalPrice }),
    ...(roundLowestFinalPrice !== undefined && { roundLowestFinalPrice }),
    duration,
    averageRating: line.averageRatings,
    logoUrl: line.logoUrl,
    copyrightProtected: line.copyrightProtected,
    lineUrl: line.ally ? null : line.redirectionInfo.mobile.shortUrl,
    hasDiscounts: !!providerDiscount,
    roundHasDiscounts: !!roundProviderDiscount,
    tripCount: 1,
    departures: [departureHourDescription],
    amenities: line.services,
    importance: line.importance,
  };
}

function getFlightProviderProperties(flight) {
  const {
    legs,
    duration,
    departure,
    services = [],
    pricing: { total, providerDiscount },
  } = flight;
  const carrier = dataFinder('carriers', legs[0].carrierId);
  const multicarrier = hasMultipleCarriers(legs);
  const departureHourDescription = hourDescription(moment(departure).hour());
  let multicarrierProperties = {};

  if (multicarrier) {
    multicarrierProperties = {
      id: 'multicarrier',
      name: 'Múltiples Aerolíneas',
      transporterName: 'Múltiples Aerolíneas',
      ally: true,
      copyrightProtected: false,
      averageRating: 0,
      logoUrl: null,
    };
  }

  return {
    transportType: 'flight',
    id: carrier.carrierId,
    name: carrier.name,
    transporterName: carrier.name,
    ally: carrier.ally,
    lowestPrice: parseInt(total.toFixed(), 10),
    duration,
    averageRating: carrier.averageRating,
    logoUrl: carrier.logoUrl,
    copyrightProtected: !!carrier.copyrightProtected,
    hasDiscounts: !!providerDiscount,
    tripCount: 1,
    departures: [departureHourDescription],
    amenities: services,
    // overwrite properties for multicarrier flights
    ...multicarrierProperties,
  };
}

function getMixedProviderProperties(itinerary) {
  const {
    duration,
    departure,
    pricing: { total },
  } = itinerary;
  const departureHourDescription = hourDescription(moment(departure).hour());
  let ally = true;
  let copyrightProtected = false;
  const mixedProviders = [];

  itinerary.fragments.forEach((fragment) => {
    let providerProperties;

    if (fragment.transportType === 'bus') {
      providerProperties = getBusProviderProperties(fragment);
    } else {
      providerProperties = getFlightProviderProperties(fragment);
    }

    ally = ally && providerProperties.ally;
    copyrightProtected = copyrightProtected || providerProperties.copyrightProtected;
    mixedProviders.push(providerProperties);
  });

  return {
    transportType: 'mixed',
    id: itinerary.providerId,
    name: 'Multicarrier',
    transporterName: 'Multicarrier',
    ally,
    lowestPrice: total,
    duration,
    averageRatings: 0,
    logoUrl: null, // fixme
    copyrightProtected,
    hasDiscounts: false,
    tripCount: 1,
    departures: [departureHourDescription], // fixme
    mixedProviders,
  };
}

function getProviderProperties(trip) {
  switch (trip.transportType) {
    case 'bus':
      return getBusProviderProperties(trip);
    case 'flight':
      return getFlightProviderProperties(trip);
    case 'mixed':
      return getMixedProviderProperties(trip);
    default:
      return {};
  }
}

/**
 * Groups a search's trips by provider. Each provider keeps its lowest one-way price (a true
 * minimum), and its round price and discounts from its first trip.
 *
 * @param {string} searchId - The search the trips belong to.
 * @param {Array} trips - The parsed trips, flights or itineraries.
 * @returns {object} The `SET_PROVIDERS` action.
 */
export function setProviders(searchId, trips) {
  const providers = {};
  trips.forEach((trip) => {
    if (trip.openTicket) return;

    const providerProperties = getProviderProperties(trip);
    const providerId = providerProperties.id;
    const provider = providers[providerId];

    if (provider) {
      // update provider properties if its already added
      const {
        lowestPrice,
        lowestFinalPrice,
        duration,
        departures: [departureHourDescription],
      } = providerProperties;

      // The final minimum is a true minimum of its own, since it can come from another trip than
      // the base one. A trip without a final figure competes at its base total, which is what the
      // final arm paints for it. Read before `lowestPrice` moves below: until a trip records a
      // final figure, the provider's final minimum is its base minimum so far. The round value is
      // never updated, like `roundLowestPrice`: a known defect mirrored so both arms compare alike.
      if (lowestFinalPrice !== undefined || provider.lowestFinalPrice !== undefined) {
        Object.assign(provider, {
          lowestFinalPrice: Math.min(
            lowestFinalPrice ?? lowestPrice,
            provider.lowestFinalPrice ?? provider.lowestPrice,
          ),
        });
      }

      if (lowestPrice < provider.lowestPrice) {
        Object.assign(provider, { lowestPrice, duration });
      }

      if (!provider.departures.includes(departureHourDescription)) {
        provider.departures.push(departureHourDescription);
      }

      Object.assign(provider, { tripCount: provider.tripCount + 1 });
    } else {
      providers[providerId] = providerProperties;
    }
  });

  return {
    type: types.SET_PROVIDERS,
    searchId,
    providers: Object.keys(providers).map((providerId) => providers[providerId]),
  };
}

export function setTransportState(transportType, searchId, pollState, tripCount) {
  if (pollState === 'finished' && !tripCount) {
    pollState = 'disabled';
  }

  return {
    type: types.SET_TRANSPORT_STATE,
    searchId,
    transportType,
    state: pollState,
  };
}

export function setDynamicPriceAttributes(search) {
  return {
    type: types.SET_DYNAMIC_ATTRIBUTES,
    dynamicPricing: search.type_of_transport.bus.dynamic_pricing,
    promotions: search.type_of_transport.bus.promotions,
  };
}

export function filterProviders(selected) {
  return {
    type: types.FILTER_PROVIDERS,
    selected,
  };
}

export function setProviderSortOrder(sortBy) {
  return {
    type: types.SET_PROVIDERS_SORT_ORDER,
    sortBy,
  };
}

export function setProviderFilterBy(filterBy) {
  return {
    type: types.SET_PROVIDERS_FILTER_BY,
    filterBy,
  };
}

/**
 * Reset the providersFilters
 * @returns action
 */
export function resetProviderFilters() {
  return {
    type: types.RESET_PROVIDERS_FILTERS,
  };
}

export function resetProviders() {
  return { type: types.RESET_PROVIDERS };
}
